/**
 * CtaBannerBlock — Call-to-action banner with headline and button
 */
import { useDispatch, useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { toggleLoginOpen } from "@/redux/meta/metaActions";
import { useFollowDrop } from "@/hooks/drop/useDropFollow";
import { SurfaceCtaConfigResponse } from "@/hooks/hub/useGetHub";
import "./CtaBannerBlock.scss";
import {ExternalLink} from "@/widgets/ExternalLink";

interface CtaBannerBlockProps {
    config?: SurfaceCtaConfigResponse;
    sectionTitle?: string;
    /** Hub URL slug — required to fulfil the FOLLOW_HUB action. */
    surfaceSlug?: string;
}

export const CtaBannerBlock = ({ config, surfaceSlug }: CtaBannerBlockProps) => {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const followDrop = useFollowDrop();

    if (!config) return null;

    const handleClick = () => {
        switch (config.buttonAction) {
            case "EXTERNAL_LINK":
                /* Rendered as an anchor below — a programmatic open is trapped
                 * by a partner WebView. Nothing to do here. */
                break;
            case "SCROLL_TO_SECTION":
                if (config.targetSectionId) {
                    document.getElementById(config.targetSectionId)?.scrollIntoView({ behavior: "smooth" });
                }
                break;
            case "LOGIN":
                dispatch(toggleLoginOpen(true));
                break;
            case "FOLLOW_HUB":
                // Following requires auth — bounce anonymous visitors to login first.
                if (!fullToken?.token) {
                    dispatch(toggleLoginOpen(true));
                } else if (surfaceSlug) {
                    followDrop.mutate({ slug: surfaceSlug });
                }
                break;
        }
    };

    return (
        <div className="cta-banner">
            <div className="cta-banner__content">
                <h2 className="cta-banner__headline">{config.headline}</h2>
                {config.subtext && <p className="cta-banner__subtext">{config.subtext}</p>}
                {config.buttonAction === "EXTERNAL_LINK"
                    ? <ExternalLink className="cta-banner__button" href={config.buttonUrl ?? ""}>
                        {config.buttonText}
                    </ExternalLink>
                    : <button
                        type="button"
                        className="cta-banner__button"
                        onClick={handleClick}
                    >
                        {config.buttonText}
                    </button>
                }
            </div>
        </div>
    );
};
